import { useMemo } from 'react';
import ReactFlow, { Background, Controls, Handle, Position } from 'react-flow-renderer';
import { Check, Loader2, Circle, Ban } from 'lucide-react';

/**
 * Renders the workflow task graph (e.g. New User Provisioning steps) as a left-to-right
 * node/edge graph. The current ("active") step pulses and its incoming edge animates so the
 * user can see which task the agent is on. Purely presentational — steps come from the
 * ticket's workflow_state.
 */

const STATUS_STYLES = {
  done: 'bg-cp-ok text-white border-cp-ok',
  active: 'bg-cp-grad text-white border-cp-violet ring-4 ring-cp-violet/30 animate-pulse',
  pending: 'bg-cp-surface text-cp-muted border-cp-line',
  skipped: 'bg-cp-surface2 text-cp-faint border-cp-line line-through',
  failed: 'bg-cp-crit text-white border-cp-crit',
};

function StatusIcon({ status }) {
  if (status === 'done') return <Check className="h-4 w-4" />;
  if (status === 'active') return <Loader2 className="h-4 w-4 animate-spin" />;
  if (status === 'skipped' || status === 'failed') return <Ban className="h-4 w-4" />;
  return <Circle className="h-4 w-4" />;
}

const StepNode = ({ data }) => {
  const status = data.status || 'pending';
  return (
    <div
      className={`px-4 py-3 rounded-xl shadow-md border-2 w-[210px] transition-all ${
        STATUS_STYLES[status] || STATUS_STYLES.pending
      }`}
    >
      <Handle type="target" position={Position.Left} style={{ opacity: 0 }} isConnectable={false} />
      <div className="flex items-center gap-2">
        <StatusIcon status={status} />
        <span className="text-[11px] font-semibold uppercase tracking-wide opacity-80">
          Step {data.index + 1}
        </span>
      </div>
      <div className="mt-1 text-sm font-medium leading-snug">{data.label}</div>
      <Handle type="source" position={Position.Right} style={{ opacity: 0 }} isConnectable={false} />
    </div>
  );
};

const nodeTypes = { step: StepNode };

export default function WorkflowTaskGraph({ steps = [], currentStep, height = 240 }) {
  const { nodes, edges } = useMemo(() => {
    const activeKey =
      currentStep || steps.find((s) => s.status === 'active')?.key || null;

    const builtNodes = steps.map((step, index) => ({
      id: step.key || `step-${index}`,
      type: 'step',
      position: { x: index * 250, y: 0 },
      data: { label: step.label, status: step.status || 'pending', index },
      draggable: false,
      connectable: false,
      selectable: false,
    }));

    const builtEdges = steps.slice(1).map((step, index) => {
      const target = step.key || `step-${index + 1}`;
      const source = steps[index].key || `step-${index}`;
      const targetActive = (step.key && step.key === activeKey) || step.status === 'active';
      const bothDone = steps[index].status === 'done' && step.status === 'done';
      return {
        id: `${source}->${target}`,
        source,
        target,
        animated: targetActive,
        style: {
          stroke: targetActive ? '#f59e0b' : bothDone ? '#22c55e' : '#cbd5e1',
          strokeWidth: 2,
        },
      };
    });

    return { nodes: builtNodes, edges: builtEdges };
  }, [steps, currentStep]);

  return (
    <div style={{ height: typeof height === 'number' ? `${height}px` : height, width: '100%' }} data-testid="workflow-task-graph">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        zoomOnScroll={false}
        panOnScroll={false}
        preventScrolling={false}
        proOptions={{ hideAttribution: true }}
      >
        <Background gap={16} color="#eef2f7" />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  );
}
